import { useMemo } from 'react';
import type { Epic } from '../../../../../api/tasks-client/task-client.type';
import type { FilterOption } from '../../../../../components/filters/filter-panel.type';
import { useRelatedTasksTabStore } from '../../../../../store/strategy-store/strategy-store';
import { searchEpics } from '../../initiative-performance.helpers';
import { useContributingEpics } from '../../initiative-performance.hooks';
import { collectTaskTypes } from '../related-tasks-tab.helpers';
import { getTaskTypeOptions } from './filters.helpers';

/**
 * Hook that returns filtered and sorted epics based on user-selected criteria
 *
 * @returns {Epic[] | []} Filtered and sorted array of epics
 */
const useFilteredEpics = (): Epic[] | [] => {
  const epics = useContributingEpics();

  const searchTerm = useRelatedTasksTabStore((state) => state.filterControls.searchTerm);
  const sortBy = useRelatedTasksTabStore((state) => state.filterControls.sortBy);
  const sortDirection = useRelatedTasksTabStore((state) => state.filterControls.sortDirection);
  const hideEmpty = useRelatedTasksTabStore((state) => state.filterControls.hideEmpty);

  const processedEpics = useMemo(() => {
    if (!epics?.length) {
      return [];
    }

    const result = searchEpics(epics, { searchTerm, sortBy, sortDirection, hideEmpty });

    return result;
  }, [epics, searchTerm, sortBy, sortDirection, hideEmpty]);

  return processedEpics;
};

/**
 * Hook that returns filter options for task types based on available tasks
 *
 * Uses the tasks from the related tasks tab store to collect unique task types
 * and convert them into filter options with values and labels
 *
 * @returns {FilterOption[]} Array of filter options containing task type values and capitalized labels
 */
const useTaskTypesOptions = (): FilterOption[] => {
  const tasks = useRelatedTasksTabStore((state) => state.tasks);

  const taskTypes = useMemo(() => {
    return collectTaskTypes(tasks);
  }, [tasks]);

  return getTaskTypeOptions(taskTypes);
};

export { useFilteredEpics, useTaskTypesOptions };
